import React from 'react';

// Public, no-login route — same tier as /print/* and /ssg/tv in App.js. Must stay reachable
// without an account: Oura's consent screen and any external link to this policy assume that.
export default function Privacy() {
  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg-app)', color: 'var(--text-primary)', fontFamily: 'Inter, sans-serif' }}>
      <div style={{ maxWidth: 760, margin: '0 auto', padding: '48px 24px 80px', lineHeight: 1.7, color: 'var(--text-secondary)' }}>
        <h1 style={{ fontSize: 28, fontWeight: 800, marginBottom: 4, color: 'var(--text-primary)' }}>HeraLabX — Privacy Policy</h1>
        <p style={{ color: 'var(--text-muted)', fontSize: 14, marginBottom: 32 }}><strong>Last updated:</strong> 24/09/2026</p>

        <p>HeraLabX is an internal data platform operated by Heracles Almelo for use by the club's players and performance/medical staff. This page explains what personal data HeraLabX collects, why, and what rights you have over it. It applies to all data processed in HeraLabX, including the Oura ring integration.</p>

        <h2 style={h2}>Who is responsible for your data</h2>
        <p>Heracles Almelo is the data controller. Questions about this policy or your data can be sent to <a href="mailto:f.vandijck@heracles.nl" style={link}>f.vandijck@heracles.nl</a>.</p>

        <h2 style={h2}>What we collect through the Oura ring</h2>
        <p>If you choose to connect your personal Oura account to HeraLabX, we receive:</p>
        <ul style={ul}>
          <li>Sleep duration and sleep efficiency</li>
          <li>Heart rate variability (HRV)</li>
          <li>Readiness score and sleep score</li>
          <li>Resting heart rate</li>
        </ul>
        <p>We do not receive your location, your Oura account credentials, or anything outside the fields listed above. We do not see when you fell asleep, why a given reading is high or low, or anything about your life outside these specific metrics.</p>

        <h2 style={h2}>Why we collect it</h2>
        <p>This data is used only to individualize your training load and recovery plan — for example, adjusting a gym session or a training load decision around a bad recovery signal. It is combined with your wellness check-in, heart-rate and jump-test data to build an overall readiness picture, as covered in the team presentation.</p>
        <p>It is not used to monitor your personal life, and it is not a disciplinary or performance-review tool.</p>

        <h2 style={h2}>Legal basis</h2>
        <ul style={ul}>
          <li>Sleep, HRV and readiness are health-related metrics, so we rely on <strong>your explicit consent</strong>, given when you connect your Oura account through HeraLabX. You can withdraw this consent at any time (see below).</li>
          <li>The broader wellness and load-monitoring programme, which does not depend on the ring, is based on the performance of your employment contract and the club's legitimate interest in managing training load safely.</li>
        </ul>

        <h2 style={h2}>Who can see it</h2>
        <p>Only performance staff involved in planning your training have access to this data within HeraLabX. It is kept separate from medical records held by the club doctor.</p>

        <h2 style={h2}>How long we keep it</h2>
        <p>Your Oura data is kept for as long as you are registered with the first team. When you leave the club, it is deleted from HeraLabX within 3 months, except where we are required to keep records for longer under Dutch law.</p>

        <h2 style={h2}>Your rights</h2>
        <p>You can, at any time:</p>
        <ul style={ul}>
          <li><strong>Disconnect your Oura account</strong> from HeraLabX — either through your Oura app's connected-apps settings, or by asking a member of the performance staff to do it for you. Once disconnected, no further data is received.</li>
          <li><strong>Request a copy</strong> of the data HeraLabX holds about you.</li>
          <li><strong>Ask us to correct or delete</strong> data that is inaccurate or no longer needed.</li>
          <li><strong>Object</strong> to this processing.</li>
        </ul>
        <p>To exercise any of these, contact <a href="mailto:f.vandijck@heracles.nl" style={link}>f.vandijck@heracles.nl</a>. You also have the right to lodge a complaint with the Autoriteit Persoonsgegevens (the Dutch data protection authority).</p>

        <h2 style={h2}>Changes to this policy</h2>
        <p>If how we use this data changes materially, we will tell players directly before the change takes effect, not just update this page.</p>
      </div>
    </div>
  );
}

const h2 = { fontSize: 18, fontWeight: 700, marginTop: 32, marginBottom: 10 };
const ul = { paddingLeft: 22, lineHeight: 1.7 };
const link = { color: '#3b82f6' };
